/**
 * Ramp Context Provider
 * Provides configuration and state management for the ramp form
 */

import React, { createContext, useContext, useReducer, useCallback, useMemo, useRef } from 'react';
import type {
  RampApiConfig,
  OnrampSupported,
  QuotesResponse,
  Quote,
  CheckoutIntent,
  Transaction,
  FieldLocksConfig,
  FlowType,
  SupportedFiat,
  SupportedCrypto,
  SupportedPaymentMethod,
  UnavailableGateway,
} from '@nowramp/sdk';
import {
  useRampConfig,
  useQuotes,
  useCheckoutIntent,
  useTransaction,
} from '../hooks/useRamp';
import { getDefaultFiatForCountry } from '../data/countries';
import {
  filterAvailableFiats,
  filterAvailableCryptos,
  filterAvailablePaymentMethods,
  reconcileSelections,
  isSelectionUnsupported,
} from '../utils/provider-filters';

// =============================================================================
// Types
// =============================================================================

export type RampStep =
  | 'confirm'   // Confirm pre-filled order (session mode)
  | 'amount'    // Select currency and amount
  | 'quotes'    // View and compare quotes
  | 'wallet'    // Enter wallet address
  | 'checkout'  // Provider checkout (iframe/redirect)
  | 'processing' // Waiting for completion
  | 'complete'  // Success
  | 'error';    // Error state

export interface RampFormState {
  step: RampStep;
  flowType: FlowType;
  fiatCurrency: string;
  fiatAmount: string;
  cryptoCurrency: string;
  cryptoAmount: string;
  network: string;
  paymentMethodId: string;
  walletAddress: string;
  walletError: string | null;
  selectedProvider: string | null;
  selectedQuote: Quote | null;
  customerId: string;
  email: string;
  country: string;
  /** How country was set: 'auto' (GeoIP), 'manual' (user pick), or '' (not set) */
  countrySource: 'auto' | 'manual' | '';
  errorMessage: string | null;
}

/** @internal exported for unit testing */
export type RampAction =
  | { type: 'SET_STEP'; step: RampStep }
  | { type: 'SET_FLOW_TYPE'; flowType: FlowType; fieldLocks?: FieldLocksConfig | null }
  | { type: 'SET_FIAT_CURRENCY'; currency: string }
  | { type: 'SET_FIAT_AMOUNT'; amount: string }
  | { type: 'SET_CRYPTO_CURRENCY'; currency: string }
  | { type: 'SET_CRYPTO_AMOUNT'; amount: string }
  | { type: 'SET_NETWORK'; network: string }
  | { type: 'SET_CRYPTO_CHAIN'; chain: string }
  | { type: 'SET_PAYMENT_METHOD'; methodId: string }
  | { type: 'SET_WALLET_ADDRESS'; address: string }
  | { type: 'SET_WALLET_ERROR'; message: string }
  | { type: 'SELECT_QUOTE'; quote: Quote }
  | { type: 'CLEAR_QUOTE' }
  | { type: 'SET_CUSTOMER_ID'; customerId: string }
  | { type: 'SET_EMAIL'; email: string }
  | { type: 'SET_COUNTRY'; country: string }
  | { type: 'SET_COUNTRY_AUTO'; country: string }
  | { type: 'SET_ERROR'; message: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESET' };

export interface RampContextValue {
  // Configuration
  apiConfig: Pick<RampApiConfig, 'apiUrl' | 'projectId'>;
  /** Base URL for static assets (crypto icons, flags). Empty string = root-relative. */
  assetBaseUrl: string;
  config: OnrampSupported | null;
  configLoading: boolean;
  configError: Error | null;

  // Form state
  state: RampFormState;
  dispatch: React.Dispatch<RampAction>;

  // Quotes
  quotes: QuotesResponse | null;
  quotesLoading: boolean;
  quotesError: Error | null;
  fetchQuotes: () => void;

  // Order
  order: CheckoutIntent | null;
  orderLoading: boolean;
  orderError: Error | null;
  createOrder: () => Promise<void>;

  // Order status
  orderStatus: Transaction | null;
  orderStatusLoading: boolean;

  // Session-aware props
  fieldLocks: FieldLocksConfig | null;
  defaultGateway: string | null;

  // Filtered lists (provider-aware cascading filters)
  availableFiats: SupportedFiat[];
  availableCryptos: SupportedCrypto[];
  availablePaymentMethods: SupportedPaymentMethod[];
  selectionUnsupported: boolean;

  // Empty quotes feedback
  quotesEmpty: boolean;
  bestUnavailableReason: string | null;

  // Validation
  amountError: string | null;

  // Navigation helpers
  goToStep: (step: RampStep) => void;
  goBack: () => void;
  canProceed: boolean;
}

// =============================================================================
// Helpers
// =============================================================================

/** Strip trailing zeros and limit decimal places (cleans backend values like "33.00000000") */
function normalizeAmount(value: string, maxDecimals: number): string {
  if (!value) return value;
  const dotIndex = value.indexOf('.');
  if (dotIndex === -1) return value;
  let result = value;
  if (result.length - dotIndex - 1 > maxDecimals) {
    result = result.slice(0, dotIndex + maxDecimals + 1);
  }
  result = result.replace(/0+$/, '').replace(/\.$/, '');
  return result;
}

/** @internal exported for unit testing */
export function isQuotesEmpty(
  quotesLoading: boolean,
  quotes: QuotesResponse | null,
  quoteParams: unknown | null,
): boolean {
  return !quotesLoading && !!quotes && !quotes.bestQuote && !!quoteParams;
}

/**
 * Pick the most relevant unavailable-gateway reason to display.
 *
 * `defaultGateway` now receives the server-authoritative `preferredGatewayId`
 * when available, falling back to the frontend heuristic (prop or config
 * value) for older backends that don't return it.
 *
 * @internal exported for unit testing
 */
export function pickBestUnavailableReason(
  gateways: UnavailableGateway[],
  defaultGateway: string | null,
): string | null {
  if (!gateways.length) return null;

  // If default/preferred gateway is unavailable, its reason is most relevant
  if (defaultGateway) {
    const match = gateways.find((g) => g.gatewayId === defaultGateway);
    if (match?.reason) return match.reason;
  }

  // Rank by reason specificity; explicit index tie-break for cross-engine safety
  const scored = gateways
    .map((g, idx) => ({ reason: g.reason, score: g.reason ? reasonScore(g.reason) : 99, idx }))
    .filter((g) => g.reason);

  if (!scored.length) return null;
  scored.sort((a, b) => a.score - b.score || a.idx - b.idx);
  return scored[0].reason;
}

function reasonScore(reason: string): number {
  const r = reason.toLowerCase();
  if (/amount|out of range for|minimum|maximum/.test(r)) return 1;
  if (/currency|pair|network|chain/.test(r)) return 2;
  if (/country|region|location/.test(r)) return 3;
  if (/does not support|unsupported/.test(r)) return 4;
  return 5;
}

/** Order statuses that represent a terminal (final) state */
export const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled', 'expired', 'refunded'];

/** @internal exported for unit testing */
export function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.includes(status);
}

// =============================================================================
// Initial State & Reducer
// =============================================================================

/** @internal exported for unit testing */
export const initialState: RampFormState = {
  step: 'amount',
  flowType: 'buy',
  fiatCurrency: 'USD',
  fiatAmount: '',
  cryptoCurrency: '',
  cryptoAmount: '',
  network: '',
  paymentMethodId: '',
  walletAddress: '',
  walletError: null,
  selectedProvider: null,
  selectedQuote: null,
  customerId: '',
  email: '',
  country: '',
  countrySource: '',
  errorMessage: null,
};

/** @internal exported for unit testing */
export function rampReducer(state: RampFormState, action: RampAction): RampFormState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step, errorMessage: null };
    case 'SET_FLOW_TYPE':
      return {
        ...state,
        flowType: action.flowType,
        fiatAmount: '',
        cryptoAmount: '',
        fiatCurrency: action.fieldLocks?.sourceCurrency?.locked ? state.fiatCurrency : '',
        cryptoCurrency: action.fieldLocks?.destinationCurrency?.locked ? state.cryptoCurrency : '',
        network: (action.fieldLocks?.network?.locked || action.fieldLocks?.destinationCurrency?.locked)
          ? state.network : '',
        paymentMethodId: '',
        selectedQuote: null,
        selectedProvider: null,
        errorMessage: null,
      };
    case 'SET_FIAT_CURRENCY':
      return { ...state, fiatCurrency: action.currency };
    case 'SET_FIAT_AMOUNT':
      return { ...state, fiatAmount: action.amount };
    case 'SET_CRYPTO_CURRENCY':
      return { ...state, cryptoCurrency: action.currency, network: '' };
    case 'SET_CRYPTO_AMOUNT':
      return { ...state, cryptoAmount: action.amount };
    case 'SET_NETWORK':
      return { ...state, network: action.network };
    case 'SET_CRYPTO_CHAIN':
      return { ...state, network: action.chain };
    case 'SET_PAYMENT_METHOD':
      return { ...state, paymentMethodId: action.methodId };
    case 'SET_WALLET_ADDRESS':
      return { ...state, walletAddress: action.address, walletError: null };
    case 'SET_WALLET_ERROR':
      return { ...state, walletError: action.message };
    case 'SELECT_QUOTE':
      return {
        ...state,
        selectedQuote: action.quote,
        selectedProvider: action.quote.gatewayId,
      };
    case 'CLEAR_QUOTE':
      return {
        ...state,
        selectedQuote: null,
        selectedProvider: null,
      };
    case 'SET_CUSTOMER_ID':
      return { ...state, customerId: action.customerId };
    case 'SET_EMAIL':
      return { ...state, email: action.email };
    case 'SET_COUNTRY':
      return { ...state, country: action.country, countrySource: 'manual' };
    case 'SET_COUNTRY_AUTO':
      return { ...state, country: action.country, countrySource: 'auto' };
    case 'SET_ERROR':
      return { ...state, step: 'error', errorMessage: action.message };
    case 'CLEAR_ERROR':
      return { ...state, errorMessage: null };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// =============================================================================
// Context
// =============================================================================

const RampContext = createContext<RampContextValue | null>(null);

export interface RampProviderProps {
  /** Project ID (required) */
  projectId: string;
  /** API base URL (optional) */
  apiUrl?: string;
  /** Base URL for static assets (crypto icons, flags). Use CDN URL for standalone/IIFE deployments. */
  assetBaseUrl?: string;
  /** Flow type: 'buy' (onramp) or 'sell' (offramp) */
  flowType?: FlowType;
  /** External customer ID */
  customerId?: string;
  /** Brand identifier for metadata tracking (e.g., sub-brand name) */
  brandId?: string;
  /**
   * Initial fiat currency. When explicitly provided (including 'USD'),
   * GeoIP auto-detection and geo fallback will NOT override this value.
   * Manual country selection by the user WILL still update fiat unless
   * fieldLocks.sourceCurrency.locked is true.
   *
   * Passing `defaultFiatCurrency={undefined}` is equivalent to omitting
   * the prop — geo-detection will apply normally.
   *
   * @default 'USD'
   */
  defaultFiatCurrency?: string;
  /** Initial crypto currency */
  defaultCryptoCurrency?: string;
  /** Initial network */
  defaultNetwork?: string;
  /** Initial fiat amount */
  defaultFiatAmount?: string;
  /** Initial crypto amount (used for sell/offramp) */
  defaultCryptoAmount?: string;
  /** Initial wallet address */
  defaultWalletAddress?: string;
  /** Default gateway ID to pre-select. Falls back to project config if not set. */
  defaultGateway?: string;
  /** Default country code (ISO alpha-2). If set, skips GeoIP auto-detection. */
  defaultCountry?: string;
  /** Field locks from session */
  fieldLocks?: FieldLocksConfig;
  /** Partner metadata forwarded to order and webhook payloads (from session metadata) */
  partnerMetadata?: Record<string, unknown>;
  /** Poll interval for order status (ms) */
  statusPollInterval?: number;
  /** Callback when order completes */
  onComplete?: (status: Transaction) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  children: React.ReactNode;
}

export function RampProvider(props: RampProviderProps) {
  // Detect if consumer explicitly passed defaultFiatCurrency (even USD)
  // props.defaultFiatCurrency !== undefined distinguishes:
  //   <RampForm> → undefined → not protected
  //   <RampForm defaultFiatCurrency="EUR"> → 'EUR' → protected
  //   <RampForm defaultFiatCurrency="USD"> → 'USD' → protected
  //   <RampForm defaultFiatCurrency={undefined}> → undefined → not protected
  const fiatProvidedRef = useRef(props.defaultFiatCurrency !== undefined);

  const {
    projectId,
    apiUrl = 'https://api.nowramp.com',
    assetBaseUrl = '',
    flowType = 'buy',
    customerId = '',
    brandId,
    defaultFiatCurrency = 'USD',
    defaultCryptoCurrency = 'BTC',
    defaultNetwork: defaultNetworkProp = '',
    defaultFiatAmount = '',
    defaultCryptoAmount = '',
    defaultWalletAddress = '',
    defaultGateway,
    defaultCountry,
    fieldLocks,
    partnerMetadata,
    statusPollInterval = 5000,
    onComplete,
    onError,
    children,
  } = props;

  const apiConfig = useMemo(() => ({ apiUrl, projectId }), [apiUrl, projectId]);

  // If no network specified but crypto is, derive a default network.
  // BTC → 'bitcoin' (canonical API ID); others → lowercase ticker (matches Banxa network IDs).
  const defaultNetwork = defaultNetworkProp || (
    defaultCryptoCurrency === 'BTC' ? 'bitcoin'
    : defaultCryptoCurrency ? defaultCryptoCurrency.toLowerCase()
    : ''
  );

  // Determine initial step: if all fields are pre-set with a gateway, go to confirm
  // For sell: wallet is optional, so don't require it
  const initialIsSell = flowType === 'sell';
  const hasAmount = initialIsSell ? !!defaultCryptoAmount : !!defaultFiatAmount;
  const hasAllFields = initialIsSell
    ? !!(hasAmount && defaultCryptoCurrency && defaultNetwork)
    : !!(hasAmount && defaultCryptoCurrency && defaultNetwork && defaultWalletAddress);
  const initialStep: RampStep = (hasAllFields && defaultGateway) ? 'confirm' : 'amount';

  // Initialize state with defaults
  // Use '250' as default fiat amount for buy if none provided
  const effectiveFiatAmount = defaultFiatAmount || (flowType === 'buy' ? '250' : '');
  const [state, dispatch] = useReducer(rampReducer, {
    ...initialState,
    step: initialStep,
    flowType,
    fiatCurrency: defaultFiatCurrency,
    cryptoCurrency: defaultCryptoCurrency,
    network: defaultNetwork,
    fiatAmount: normalizeAmount(effectiveFiatAmount, 2),
    cryptoAmount: normalizeAmount(defaultCryptoAmount, 6),
    walletAddress: defaultWalletAddress,
    customerId,
    // If defaultCountry is provided, set it immediately with 'manual' source to skip auto-detection
    ...(defaultCountry ? { country: defaultCountry.toLowerCase(), countrySource: 'manual' as const } : {}),
  });

  const isSell = state.flowType === 'sell';

  // Fetch supported config (pass orderType for sell to get sell-specific currencies/limits)
  // Pass country so backend filters results by region
  const {
    config,
    loading: configLoading,
    error: configError,
  } = useRampConfig(apiConfig, state.flowType, state.country || undefined);

  // GeoIP auto-detection: when config loads with detectedCountry and no country is set yet
  const geoAppliedRef = useRef(false);
  React.useEffect(() => {
    if (geoAppliedRef.current) return;
    if (!config?.detectedCountry) return;
    // Skip if country already set (defaultCountry prop or manual selection)
    if (state.country) return;

    geoAppliedRef.current = true;
    const detected = config.detectedCountry.toLowerCase();
    dispatch({ type: 'SET_COUNTRY_AUTO', country: detected });

    // Auto-set fiat currency based on detected country
    const supportedFiats = config.fiats?.map((f) => f.code);
    const fiat = getDefaultFiatForCountry(detected, supportedFiats);
    if (fiat !== state.fiatCurrency && !fieldLocks?.sourceCurrency?.locked && !fiatProvidedRef.current) {
      dispatch({ type: 'SET_FIAT_CURRENCY', currency: fiat });
    }
  }, [config, state.country, state.fiatCurrency, fieldLocks]);

  // When defaultCountry is set and config loads, auto-set fiat for that country
  const defaultCountryAppliedRef = useRef(false);
  React.useEffect(() => {
    if (defaultCountryAppliedRef.current) return;
    if (!defaultCountry || !config?.fiats?.length) return;

    defaultCountryAppliedRef.current = true;
    const supportedFiats = config.fiats.map((f) => f.code);
    const fiat = getDefaultFiatForCountry(defaultCountry, supportedFiats);
    if (fiat !== state.fiatCurrency && !fieldLocks?.sourceCurrency?.locked && !fiatProvidedRef.current) {
      dispatch({ type: 'SET_FIAT_CURRENCY', currency: fiat });
    }
  }, [defaultCountry, config, state.fiatCurrency, fieldLocks]);

  // Reset geo-fallback guard when user manually changes country, so the
  // fallback can re-evaluate for the new geo-detected currency
  // Manual country change: auto-update fiat currency when user picks a new country
  const prevCountryRef = useRef(state.country);
  React.useEffect(() => {
    if (state.country === prevCountryRef.current) return;
    prevCountryRef.current = state.country;
    if (state.countrySource === 'manual') {
      geoFallbackAppliedRef.current = false;
    }

    // Only auto-switch fiat for manual selections (not initial auto-detection, which is handled above)
    if (state.countrySource !== 'manual') return;
    if (!config?.fiats?.length) return;

    const supportedFiats = config.fiats.map((f) => f.code);
    const fiat = getDefaultFiatForCountry(state.country, supportedFiats);
    if (fiat !== state.fiatCurrency && !fieldLocks?.sourceCurrency?.locked) {
      dispatch({ type: 'SET_FIAT_CURRENCY', currency: fiat });
    }
  }, [state.country, state.countrySource, config, state.fiatCurrency, fieldLocks]);

  // When fiat currency changes, ensure the amount meets the new currency's minimum
  // This handles cases like geo-detection switching from USD (250 is fine) to TRY (250 is below min)
  const prevFiatRef = useRef(state.fiatCurrency);
  React.useEffect(() => {
    if (state.fiatCurrency === prevFiatRef.current) return;
    prevFiatRef.current = state.fiatCurrency;

    if (isSell || !config?.fiats?.length || !state.fiatAmount) return;

    const fiat = config.fiats.find((f) => f.code === state.fiatCurrency);
    if (!fiat?.minAmount) return;

    const currentAmount = parseFloat(state.fiatAmount);
    const minAmount = parseFloat(fiat.minAmount);
    if (!isNaN(currentAmount) && !isNaN(minAmount) && currentAmount < minAmount) {
      dispatch({ type: 'SET_FIAT_AMOUNT', amount: String(Math.ceil(minAmount)) });
    }
  }, [state.fiatCurrency, state.fiatAmount, config, isSell]);

  // --- Cascading provider-aware filters ---
  const { availableFiats, availableCryptos, availablePaymentMethods } = useMemo(() => {
    if (!config) {
      return { availableFiats: [] as SupportedFiat[], availableCryptos: [] as SupportedCrypto[], availablePaymentMethods: [] as SupportedPaymentMethod[] };
    }

    // Resolve selected network's providers
    let networkProviders: string[] | undefined;
    if (state.cryptoCurrency && state.network) {
      const crypto = config.cryptos?.find((c) => c.code === state.cryptoCurrency);
      const network = crypto?.networks?.find((n) => n.id === state.network);
      if (!crypto || !network) {
        networkProviders = [];
      } else {
        networkProviders = network.providers;
      }
    }

    // Resolve selected fiat's providers
    let fiatProviders: string[] | undefined;
    if (state.fiatCurrency) {
      const fiat = config.fiats?.find((f) => f.code === state.fiatCurrency);
      if (!fiat) {
        fiatProviders = [];
      } else {
        fiatProviders = fiat.providers;
      }
    }

    return {
      availableFiats: filterAvailableFiats(config.fiats ?? [], networkProviders),
      availableCryptos: filterAvailableCryptos(config.cryptos ?? [], fiatProviders),
      availablePaymentMethods: filterAvailablePaymentMethods(
        config.paymentMethods ?? [], networkProviders, state.fiatCurrency
      ),
    };
  }, [config, state.cryptoCurrency, state.network, state.fiatCurrency]);

  // Derived flag: locked selection has no provider support
  const selectionUnsupported = useMemo(() => {
    if (configLoading || !config) return false;
    return isSelectionUnsupported(
      config,
      { cryptoCurrency: state.cryptoCurrency, network: state.network, fiatCurrency: state.fiatCurrency },
      {
        cryptoLocked: !!fieldLocks?.destinationCurrency?.locked,
        networkLocked: !!fieldLocks?.network?.locked,
        fiatLocked: !!fieldLocks?.sourceCurrency?.locked,
      },
      { availableFiats, availableCryptos, availablePaymentMethods }
    );
  }, [configLoading, config, fieldLocks, state.cryptoCurrency, state.network,
      state.fiatCurrency, availableFiats, availableCryptos, availablePaymentMethods]);

  // Reconcile selections when filtered lists change
  React.useEffect(() => {
    if (configLoading) return;
    if (availableFiats.length === 0 && availableCryptos.length === 0) return;

    const corrections = reconcileSelections(
      {
        cryptoCurrency: state.cryptoCurrency,
        network: state.network,
        fiatCurrency: state.fiatCurrency,
        paymentMethodId: state.paymentMethodId,
      },
      { availableFiats, availableCryptos, availablePaymentMethods },
      {
        cryptoLocked: !!fieldLocks?.destinationCurrency?.locked,
        networkLocked: !!fieldLocks?.network?.locked,
        fiatLocked: !!fieldLocks?.sourceCurrency?.locked,
      }
    );

    if (!corrections) return;

    if (corrections.cryptoCurrency !== undefined) {
      dispatch({ type: 'SET_CRYPTO_CURRENCY', currency: corrections.cryptoCurrency });
    }
    if (corrections.network !== undefined) {
      dispatch({ type: 'SET_NETWORK', network: corrections.network });
    }
    if (corrections.fiatCurrency !== undefined) {
      dispatch({ type: 'SET_FIAT_CURRENCY', currency: corrections.fiatCurrency });
    }
    if (corrections.paymentMethodId !== undefined) {
      dispatch({ type: 'SET_PAYMENT_METHOD', methodId: corrections.paymentMethodId });
    }
  }, [configLoading, availableFiats, availableCryptos, availablePaymentMethods,
      state.cryptoCurrency, state.network, state.fiatCurrency, state.paymentMethodId,
      fieldLocks, dispatch]);

  // Amount validation against min/max limits
  // Computed before quoteParams so invalid amounts never trigger a fetch
  // For sell, user enters crypto amount — skip fiat-based validation (backend validates)
  const amountError = useMemo(() => {
    if (isSell) {
      // For sell, basic presence check only — backend validates crypto amount limits
      if (!state.cryptoAmount) return null;
      const amount = parseFloat(state.cryptoAmount);
      if (isNaN(amount) || amount <= 0) return 'Enter a valid amount';
      return null;
    }

    if (!config || !state.fiatAmount) return null;

    const amount = parseFloat(state.fiatAmount);
    if (isNaN(amount)) return null;

    const fiat = config.fiats.find((f) => f.code === state.fiatCurrency);
    if (!fiat) return null;

    const min = parseFloat(fiat.minAmount);
    const max = parseFloat(fiat.maxAmount);

    if (!isNaN(min) && amount < min) {
      return `Minimum amount is ${fiat.minAmount} ${state.fiatCurrency}`;
    }
    if (!isNaN(max) && amount > max) {
      return `Maximum amount is ${fiat.maxAmount} ${state.fiatCurrency}`;
    }
    return null;
  }, [config, state.fiatAmount, state.fiatCurrency, state.cryptoAmount, isSell]);

  // Fetch quotes when form is filled, config loaded, and amount is valid
  // Wait for geo-detection to resolve before fetching (prevents wasted initial fetch
  // that gets cancelled when fiat currency changes from USD to the detected currency)
  const quoteParams = useMemo(() => {
    const hasAmount = isSell ? !!state.cryptoAmount : !!state.fiatAmount;
    if (!config || !hasAmount || !state.fiatCurrency || !state.cryptoCurrency || !state.network) {
      return null;
    }
    if (amountError) {
      return null;
    }
    // If backend detected a country but geo-detection effect hasn't applied yet, wait
    if (config.detectedCountry && state.countrySource === '' && !defaultCountry) {
      return null;
    }
    return {
      fiatCurrency: state.fiatCurrency,
      ...(isSell ? { cryptoAmount: state.cryptoAmount } : { fiatAmount: state.fiatAmount }),
      cryptoCurrency: state.cryptoCurrency,
      network: state.network,
      paymentMethodId: state.paymentMethodId || undefined,
      country: state.country || undefined,
      orderType: state.flowType,
    };
  }, [config, state.fiatCurrency, state.fiatAmount, state.cryptoAmount, state.cryptoCurrency, state.network, state.paymentMethodId, state.country, state.countrySource, amountError, isSell, state.flowType, defaultCountry]);

  const {
    quotes,
    loading: quotesLoading,
    error: quotesError,
    refetch: fetchQuotes,
  } = useQuotes(apiConfig, quoteParams);

  const quotesEmpty = isQuotesEmpty(quotesLoading, quotes, quoteParams);

  // Fallback: if geo-detected fiat returns no quotes for the default crypto, fall back to USD
  // This handles cases where a local currency (e.g. NGN) has no provider pairs with BTC
  // Only triggers when quotes loaded successfully with zero results — never on API errors
  const geoFallbackAppliedRef = useRef(false);
  React.useEffect(() => {
    if (geoFallbackAppliedRef.current) return;
    // Only apply for auto-detected currencies, not manual selections
    if (state.countrySource !== 'auto') return;
    if (fieldLocks?.sourceCurrency?.locked || fiatProvidedRef.current) return;
    // Wait until quotes have actually loaded (not loading, not null params)
    if (quotesLoading || !quoteParams) return;
    // Skip fallback on API errors — only fall back when the response succeeded
    // but contained no quotes (i.e. quotes object exists with no bestQuote)
    if (quotesError || !quotes) return;
    // If we got quotes, no fallback needed
    if (quotes.bestQuote) return;
    // If the fiat is already USD, nothing to fall back to
    if (state.fiatCurrency === 'USD') return;

    geoFallbackAppliedRef.current = true;
    dispatch({ type: 'SET_FIAT_CURRENCY', currency: 'USD' });
    dispatch({ type: 'SET_FIAT_AMOUNT', amount: '250' });
  }, [quotes, quotesLoading, quotesError, quoteParams, state.countrySource, state.fiatCurrency]);

  // Order creation
  const {
    order,
    loading: orderLoading,
    error: orderError,
    createOrder: createOrderApi,
  } = useCheckoutIntent(apiConfig);

  // Order status polling — delay the initial fetch to give the checkout
  // iframe time to load before we start polling.  Without this, the first
  // poll can return a 'processing' status from the provider and the
  // CheckoutStep immediately transitions to ProcessingStep before the
  // user even sees the iframe (race condition with iframe-based providers
  // like Banxa that move to 'processing' quickly).
  const CHECKOUT_POLL_INITIAL_DELAY = 15_000; // 15 seconds

  const {
    status: orderStatus,
    loading: orderStatusLoading,
  } = useTransaction(
    { apiUrl },
    order?.orderId ?? null,
    { pollInterval: statusPollInterval, initialDelay: CHECKOUT_POLL_INITIAL_DELAY }
  );

  // Handle order completion — fire once per order for all terminal statuses
  const terminalFiredForRef = useRef<string | null>(null);
  React.useEffect(() => {
    if (!orderStatus?.status || !onComplete) return;
    if (!TERMINAL_STATUSES.includes(orderStatus.status)) return;
    if (terminalFiredForRef.current === orderStatus.orderId) return;
    terminalFiredForRef.current = orderStatus.orderId;
    onComplete(orderStatus);
  }, [orderStatus?.status, orderStatus?.orderId, onComplete]);

  // Handle errors — only propagate config and order errors as fatal.
  // Quote errors are non-fatal (validation rejections, transient failures)
  // and are already surfaced via quotesError in the UI.
  React.useEffect(() => {
    const error = configError || orderError;
    if (error && onError) {
      onError(error);
    }
  }, [configError, orderError, onError]);

  // Resolve effective default gateway: explicit prop > project config
  const effectiveDefaultGateway = defaultGateway || config?.defaultGateway || null;

  const bestUnavailableReason = useMemo(() => {
    if (!quotesEmpty || !quotes?.unavailableGateways) return null;
    // Prefer server-resolved preferred gateway; fall back to frontend heuristic
    const preferred = quotes.preferredGatewayId || effectiveDefaultGateway;
    return pickBestUnavailableReason(quotes.unavailableGateways, preferred);
  }, [quotesEmpty, quotes, effectiveDefaultGateway]);

  // Use refs to avoid stale closures — async data loads mean callbacks may
  // capture earlier null values even with useCallback deps.
  const gatewayRef = useRef(effectiveDefaultGateway);
  gatewayRef.current = effectiveDefaultGateway;

  const quotesRef = useRef(quotes);
  quotesRef.current = quotes;

  const brandIdRef = useRef(brandId);
  brandIdRef.current = brandId;

  // Create order handler
  const createOrder = useCallback(async () => {
    const gateway = state.selectedQuote?.gatewayId || quotesRef.current?.bestQuote?.gatewayId || gatewayRef.current;
    if (!gateway || !state.walletAddress) {
      const missing = [!gateway && 'gateway', !state.walletAddress && 'walletAddress'].filter(Boolean).join(', ');
      dispatch({ type: 'SET_ERROR', message: `Missing required fields: ${missing}` });
      return;
    }

    try {
      await createOrderApi({
        gateway,
        customerId: state.customerId || undefined,
        fiatCurrency: state.fiatCurrency,
        ...(isSell
          ? { cryptoAmount: state.cryptoAmount }
          : { fiatAmount: state.fiatAmount }
        ),
        cryptoCurrency: state.cryptoCurrency,
        network: state.network,
        walletAddress: state.walletAddress,
        paymentMethodId: state.paymentMethodId || undefined,
        email: state.email || undefined,
        orderType: state.flowType,
        ...(brandIdRef.current ? { metadata: { brand: brandIdRef.current } } : {}),
        ...(partnerMetadata && Object.keys(partnerMetadata).length > 0 ? { partnerMetadata } : {}),
      });

      // Move to checkout step
      dispatch({ type: 'SET_STEP', step: 'checkout' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create order';
      // Wallet address validation errors stay inline instead of going to error screen
      if (/wallet|address/i.test(message)) {
        dispatch({ type: 'SET_WALLET_ERROR', message: message.replace(/^Failed to create checkout intent:\s*/i, '') });
      } else {
        dispatch({ type: 'SET_ERROR', message });
      }
    }
  }, [state, createOrderApi, isSell, partnerMetadata]);

  // Navigation helpers
  const goToStep = useCallback((step: RampStep) => {
    dispatch({ type: 'SET_STEP', step });
  }, []);

  const goBack = useCallback(() => {
    const stepOrder: RampStep[] = ['confirm', 'amount', 'quotes', 'wallet', 'checkout', 'processing', 'complete'];
    const currentIndex = stepOrder.indexOf(state.step);
    if (currentIndex > 0) {
      dispatch({ type: 'SET_STEP', step: stepOrder[currentIndex - 1] });
    }
  }, [state.step, isSell]);

  // Check if user can proceed to next step
  const canProceed = useMemo(() => {
    const hasAmount = isSell ? !!state.cryptoAmount : !!state.fiatAmount;
    switch (state.step) {
      case 'confirm':
        return !!(hasAmount && state.cryptoCurrency && state.network && state.walletAddress && !amountError);
      case 'amount':
        return !!(hasAmount && state.cryptoCurrency && state.network && !amountError);
      case 'quotes':
        return !!state.selectedQuote;
      case 'wallet':
        return !!state.walletAddress;
      default:
        return false;
    }
  }, [state, amountError, isSell]);

  const value: RampContextValue = {
    apiConfig,
    assetBaseUrl,
    config,
    configLoading,
    configError,
    state,
    dispatch,
    quotes,
    quotesLoading,
    quotesError,
    fetchQuotes,
    order,
    orderLoading,
    orderError,
    createOrder,
    orderStatus,
    orderStatusLoading,
    fieldLocks: fieldLocks || null,
    defaultGateway: effectiveDefaultGateway,
    availableFiats,
    availableCryptos,
    availablePaymentMethods,
    selectionUnsupported,
    quotesEmpty,
    bestUnavailableReason,
    amountError,
    goToStep,
    goBack,
    canProceed,
  };

  return (
    <RampContext.Provider value={value}>
      {children}
    </RampContext.Provider>
  );
}

/**
 * Hook to access ramp context
 */
export function useRamp(): RampContextValue {
  const context = useContext(RampContext);
  if (!context) {
    throw new Error('useRamp must be used within a RampProvider');
  }
  return context;
}
